import React, { useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Sparkles, Loader2, Copy, Check, RefreshCw } from 'lucide-react';
import { ai } from '../lib/api';
import { toast } from 'sonner';

// Reusable "Draft with AI" button. Opens a dialog that drafts a customer message
// via the AI endpoint (works in template mode too), with Copy and optional "Use this".
// Props: { kind='email', context, customerName, tone, onUse, label, size, variant, className }
export default function AiDraftButton({ kind = 'email', context = '', customerName = '', tone, onUse, label = 'Draft with AI', size = 'sm', variant = 'outline', className = '' }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState('');
  const [copied, setCopied] = useState(false);

  const gen = async () => {
    setBusy(true);
    try {
      const body = { kind, context: typeof context === 'function' ? context() : context, customerName };
      if (tone) body.tone = tone;
      const r = await ai.draft(body);
      setText(r.text || '');
    } catch { toast.error('Could not draft the message'); }
    finally { setBusy(false); }
  };

  const openAndGen = () => { setOpen(true); setText(''); gen(); };
  const copy = async () => { try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { toast.error('Copy failed'); } };
  const use = () => { onUse?.(text); setOpen(false); toast.success('Draft inserted'); };

  return (
    <>
      <Button type="button" size={size} variant={variant} onClick={openAndGen} className={`gap-1.5 ${className}`}>
        <Sparkles className="w-4 h-4" />{label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#8dd110]" />AI Draft</DialogTitle></DialogHeader>
          {busy ? (
            <div className="py-10 flex items-center justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={9} className="w-full text-sm border border-slate-200 rounded-md p-3" />
          )}
          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="outline" onClick={gen} disabled={busy} className="gap-1.5"><RefreshCw className="w-4 h-4" />Regenerate</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={copy} disabled={!text} className="gap-1.5">{copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}{copied ? 'Copied' : 'Copy'}</Button>
              {onUse && <Button onClick={use} disabled={!text} className="bg-[#8dd110] hover:bg-[#71a80f] gap-1.5"><Check className="w-4 h-4" />Use this</Button>}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
